import { parseQRStyleConfig } from '@/lib/qr-sdk'
import type {
  Action,
  ActionType,
  Alias,
  Design,
  Group,
  MemberRole,
  QrCode,
  Schedule,
  ScheduleRepeat,
  ScheduleTask,
} from '@/lib/types'
import type {
  GroupFieldsFragment,
  QrCodeFieldsFragment,
  ScheduleFieldsFragment,
} from '@/graphql/generated'

/**
 * pg_graphql shapes -> the app's view models.
 *
 * This file is the entire reason no component imports from `@/graphql`. Keeping
 * the translation in one place means a column rename is a change here, not a
 * change in thirty components — and it is what let the whole UI be built against
 * fixtures before any of this existed.
 *
 * Every mapper is pure and total: a missing relation becomes a sensible empty
 * value rather than an exception, because a half-populated row should render a
 * degraded card, not blank the screen.
 */

/** `edges { node }` is noise at every call site. */
function nodes<T>(collection: { edges: { node: T }[] } | null | undefined): T[] {
  return collection?.edges.map((edge) => edge.node) ?? []
}

/**
 * The node type inside a generated collection field.
 *
 * pg_graphql types every nested collection as nullable, so reaching for
 * `Fragment['xCollection']['edges'][number]['node']` does not compile. This
 * unwraps the null and the edge wrapper in one step.
 */
type NodeOf<TCollection> =
  NonNullable<TCollection> extends { edges: { node: infer TNode }[] }
    ? TNode
    : never

/* ---------------------------------------------------------------------------
 * Lookup codes
 *
 * These are seeded rows, not enums, so the codes are matched explicitly and
 * anything unrecognized falls back rather than throwing. A lookup table gaining
 * a row should not white-screen the app.
 * ------------------------------------------------------------------------ */

/** `action_types.code` -> the two kinds the UI knows how to render. */
function actionType(code: string | null | undefined): ActionType {
  return code === 'send_text_message' ? 'text' : 'link'
}

/**
 * `schedule_types.code` -> the two repeats that have a working editor.
 *
 * The table also seeds monthly, yearly and does-not-repeat. The prototype never
 * built editors for those, so they render as their nearest neighbour rather
 * than crashing the schedules screen; a schedule the UI cannot express is still
 * listed, and still togglable.
 */
function scheduleRepeat(code: string | null | undefined): ScheduleRepeat {
  return code === 'repeats_weekly' ? 'weekly' : 'daily'
}

/**
 * `organization_member_roles.code` -> the label the UI shows.
 *
 * The database seeds three roles; the prototype's type carries a fourth,
 * `Viewer`, which has no row. It stays in the union because the roles table is
 * where a Viewer would be added, and the mapper would then need no change.
 */
export function memberRole(code: string | null | undefined): MemberRole {
  switch (code) {
    case 'owner':
      return 'Owner'
    case 'administrator':
      return 'Admin'
    case 'viewer':
      return 'Viewer'
    default:
      return 'Member'
  }
}

/* ---------------------------------------------------------------------------
 * People
 * ------------------------------------------------------------------------ */

export function fullName(
  user: { firstName?: string | null; lastName?: string | null } | null | undefined,
): string {
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
  return name || 'Unknown'
}

/** "Charlie Hill" -> "CH". Falls back to the first two characters of an email. */
export function initialsOf(name: string, email?: string | null): string {
  const words = name.trim().split(/\s+/).filter(Boolean)

  if (words.length >= 2) {
    return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase()
  }
  if (words.length === 1 && words[0] !== 'Unknown') {
    return words[0].slice(0, 2).toUpperCase()
  }

  return (email ?? '?').slice(0, 2).toUpperCase()
}

/* ---------------------------------------------------------------------------
 * QR codes
 * ------------------------------------------------------------------------ */

function toAction(node: NodeOf<QrCodeFieldsFragment['actionsCollection']>): Action {
  const type = actionType(node.actionType?.code)

  return {
    id: node.id,
    name: node.name,
    type,
    /* Kept undefined rather than null for the field that does not apply, so the
       forms can treat "absent" and "empty string" as different things. */
    linkToUrl: type === 'link' ? (node.linkToUrl ?? undefined) : undefined,
    textPhoneNumber:
      type === 'text' ? (node.textPhoneNumber ?? undefined) : undefined,
  }
}

/**
 * A short link is "custom" when the user chose it.
 *
 * `alias_types.is_user_editable` is the real signal, not the code: the two
 * seeded types are `an4` (a generated 4-character alias) and `f22` (a legacy
 * Firestore-era id), and both of those codes are meaningless to a user.
 */
function toAlias(node: NodeOf<QrCodeFieldsFragment['aliasesCollection']>): Alias {
  return {
    id: node.id,
    alias: node.alias,
    type: node.aliasType?.isUserEditable ? 'custom' : 'generated',
  }
}

function toDesign(node: NodeOf<QrCodeFieldsFragment['novasCollection']>): Design {
  return {
    id: node.id,
    name: node.name,
    color: node.color ?? null,
    isQrCode: node.novaType?.code === 'qr_code',
    style: parseQRStyleConfig(node.qrStyle),
    embeddedLink: node.embeddedLink ?? null,
    embeddedAliasId: node.embeddedAliasId ?? null,
    decorationImageUrl: node.novaDecorationImage?.imageUrl ?? null,
  }
}

/*
 * `novas.qr_style` is parsed by the SDK's `parseQRStyleConfig`, not by a local
 * copy of the same logic.
 *
 * It is PARSED, never cast: pg_graphql serialises jsonb to a STRING, so the
 * value arriving here is `'{"dotsType":"classy",...}'`, and casting it would
 * typecheck and then read `undefined` off every property. The codegen scalar map
 * types it as `string` precisely to force this.
 *
 * The SDK's version is the one the legacy app already used and the one covered
 * by tests, and it handles the case this file's own parser had to hand-roll:
 * a value that arrives already decoded, which is what happens on the API side
 * where TypeORM parses jsonb for you. Malformed content becomes `null` — an
 * unstyled code — rather than throwing inside a preview.
 */

export function toScheduleTask(
  node: NodeOf<ScheduleFieldsFragment['scheduleTasksCollection']>,
): ScheduleTask {
  return {
    id: node.id,
    /* An empty cron would make `lib/schedules.ts` parse nothing and the timeline
       render an undefined time. Midnight daily is the neutral stand-in. */
    cron: node.cronSchedule || '0 0 * * *',
    actionId: node.actionId ?? null,
  }
}

export function toSchedule(node: ScheduleFieldsFragment): Schedule {
  return {
    id: node.id,
    name: node.name,
    isActive: node.isActive ?? false,
    repeat: scheduleRepeat(node.scheduleType?.code),
    /* The schedules screen renders times in this zone; falling back to the
       browser's is closer to right than falling back to UTC. */
    timeZone:
      node.timeZone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      'UTC',
    tasks: nodes(node.scheduleTasksCollection).map(toScheduleTask),
  }
}

/**
 * The full view model for a QR code.
 *
 * Scan counts are NOT here. They come from the analytics endpoints, which
 * aggregate the events tables, and are merged in by the components that show
 * them — carrying zeroes through this mapper would render "0 scans" as though
 * it were measured rather than simply not loaded yet.
 */
export function toQrCode(
  node: QrCodeFieldsFragment & {
    schedulesCollection?: { edges: { node: ScheduleFieldsFragment }[] } | null
  },
): QrCode {
  const actions = nodes(node.actionsCollection).map(toAction)

  return {
    id: node.id,
    name: node.name,
    /* `constellations.icon` is a nullable FK to `icons.name`. The catalog has a
       fallback for an unknown name, so an empty string is safe here. */
    icon: node.icon ?? '',
    color: node.color,
    groupIds: nodes(node.groupConstellationLinksCollection)
      .map((link) => link.groupId)
      .filter((id): id is string => Boolean(id)),
    isArchived: node.isArchived,
    /*
     * NOT defaulted to the first action.
     *
     * It used to be, carried over from the fixtures on the theory that "a code
     * with destinations but no active one still resolves". It does not:
     * `RedirectorService` reads `constellation.activeAction` and, finding none,
     * sends the scan to the generic fallback URL (see the `if (!activeAction)`
     * branch). So the invented default painted a destination green and "live"
     * for a code that actually resolved nowhere.
     *
     * It also made the control panel inert. The panel skips the write when you
     * click whatever it believes is already live, so clicking the *displayed*
     * live destination on such a code sent no request at all — the click that
     * would have fixed the problem was the one click guaranteed to do nothing.
     */
    activeActionId: node.activeActionId ?? null,
    defaultQrCodeNovaId: node.defaultQrCodeNovaId ?? null,
    defaultWebLinkNovaId: node.defaultWebLinkNovaId ?? null,
    actions,
    aliases: nodes(node.aliasesCollection).map(toAlias),
    designs: nodes(node.novasCollection).map(toDesign),
    schedules: nodes(node.schedulesCollection).map(toSchedule),
    scans7d: 0,
    scans30d: 0,
    scansTotal: 0,
    lastScanAt: null,
    createdAt: node.createdAt,
    updatedBy: fullName(node.updatedByUser),
  }
}

export function toGroup(node: GroupFieldsFragment): Group {
  return {
    id: node.id,
    name: node.name,
    icon: node.icon ?? '',
    color: node.color,
    parentGroupId: node.parentGroupId ?? null,
  }
}

export { nodes }
